import type {
  BlocksViewCommands,
  BlocksViewSettings,
  BlocksViewModelProperties,
  BlocksViewPropertiesWithModifiers,
  LinksAggregation,
} from '../types';
import { ViewIds } from '@ardoq/api-types';
import {
  DropdownOptionType,
  isDropdownOption,
  type DropdownOption,
} from '@ardoq/dropdown-menu';
import {
  DropdownConfig,
  isDropdownConfig,
  SettingsType,
} from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import { componentStyleDropdown } from 'tabview/relationshipDiagrams/leftMenuItems';
import { t, type I18nLocale } from '@ardoq/i18n';

type LinksAggregationOptionConfig = {
  longLabelKey: string;
  longLabelDefault: string;
  shortLabelKey: string;
  shortLabelDefault: string;
};

const LINKS_AGGREGATION_OPTIONS: Record<
  LinksAggregation,
  LinksAggregationOptionConfig
> = {
  byReferenceType: {
    longLabelKey: 'appLayout:viewPaneHeader.combineReferenceLines',
    longLabelDefault: 'Combine reference lines',
    shortLabelKey: 'appLayout:viewPaneHeader.combine',
    shortLabelDefault: 'Combine',
  },
  separate: {
    longLabelKey: 'appLayout:viewPaneHeader.separateReferenceLines',
    longLabelDefault: 'Separate reference lines',
    shortLabelKey: 'appLayout:viewPaneHeader.separate',
    shortLabelDefault: 'Separate',
  },
  hide: {
    longLabelKey: 'appLayout:viewPaneHeader.hideReferenceLines',
    longLabelDefault: 'Hide reference lines',
    shortLabelKey: 'appLayout:viewPaneHeader.hide',
    shortLabelDefault: 'Hide',
  },
};

const LINKS_AGGREGATION_ORDER: LinksAggregation[] = [
  'byReferenceType',
  'separate',
  'hide',
];

export const isActiveLinksAggregation = (
  linksAggregation: LinksAggregation,
  current: BlocksViewSettings['linksAggregation']
): boolean => {
  if (linksAggregation === 'separate') {
    return !current || current === 'separate';
  }
  return current === linksAggregation;
};

type GetViewModifiersArgs = {
  viewSettings: BlocksViewSettings;
  commands: BlocksViewCommands;
  i18nLocale: I18nLocale;
};

const getViewModifiers = ({
  viewSettings,
  commands,
  i18nLocale,
}: GetViewModifiersArgs): DropdownConfig[] => [
  {
    id: 'linksAggregation',
    label: t(
      i18nLocale,
      'appLayout:viewPaneHeader.referenceLines',
      'Reference lines'
    ),
    iconName: IconName.SEPARATE_REFERENCES,
    type: SettingsType.DROPDOWN,
    options: LINKS_AGGREGATION_ORDER.map(linksAggregation => {
      const option = LINKS_AGGREGATION_OPTIONS[linksAggregation];
      return {
        name: linksAggregation,
        label: t(i18nLocale, option.longLabelKey, option.longLabelDefault),
        type: DropdownOptionType.OPTION,
        isActive: isActiveLinksAggregation(
          linksAggregation,
          viewSettings.linksAggregation
        ),
        onClick: () => commands.updateViewSettings({ linksAggregation }),
      } satisfies DropdownOption;
    }),
  },
  componentStyleDropdown(
    ViewIds.BLOCKS,
    viewSettings,
    (_viewId, settings) => commands.updateViewSettings(settings),
    i18nLocale
  ),
];

export const enhanceViewPropertiesWithViewModifiers = (
  viewProperties: BlocksViewModelProperties,
  commands: BlocksViewCommands
): BlocksViewPropertiesWithModifiers => ({
  ...viewProperties,
  viewModifiers: getViewModifiers({
    viewSettings: viewProperties.viewSettings,
    commands,
    i18nLocale: viewProperties.i18nLocale,
  }),
});

const BLOCKS_VIEW_PROPERTIES_KEYS: (keyof BlocksViewPropertiesWithModifiers)[] =
  [
    'viewSettings',
    'graph',
    'componentTypes',
    'referenceModelTypes',
    'errors',
    'hasClones',
    'viewModifiers',
  ];

export const isBlocksViewPropertiesWithViewModifiers = (
  viewProperties: Record<string, any>
): viewProperties is BlocksViewPropertiesWithModifiers =>
  BLOCKS_VIEW_PROPERTIES_KEYS.every(key => key in viewProperties);

const isLinksAggregation = (
  name: string | undefined
): name is LinksAggregation =>
  name !== undefined && name in LINKS_AGGREGATION_OPTIONS;

const getShortLabelForLinksAggregation = (
  name: string | undefined,
  i18nLocale: I18nLocale
): string => {
  if (!isLinksAggregation(name)) return '';
  const option = LINKS_AGGREGATION_OPTIONS[name];
  return t(i18nLocale, option.shortLabelKey, option.shortLabelDefault);
};

export const findActiveLinksAggregationTagLabel = (
  viewProperties: Record<string, any>,
  i18nLocale: I18nLocale
): string => {
  if (!isBlocksViewPropertiesWithViewModifiers(viewProperties)) {
    return '';
  }
  const selectedField = viewProperties.viewModifiers
    .filter(isDropdownConfig)
    .find(viewModifier => viewModifier.id === 'linksAggregation');

  const activeOption = selectedField?.options
    .filter(isDropdownOption)
    .find(option => option.isActive);

  return getShortLabelForLinksAggregation(activeOption?.name, i18nLocale);
};
